<script>
    import { writable } from 'svelte/store';
    import { extent } from 'd3-array';
    import { geoMercator, geoPath } from 'd3-geo';
    import { schemeCategory10 } from 'd3-scale-chromatic';
    import { scaleLinear, scaleOrdinal } from 'd3-scale';
    import { getContext } from 'svelte';
    import { fade } from 'svelte/transition';

    // --- Properties
    export let dataKey;
    const width = 600;
    const height = 370;
    const zoomSpeed = 1.1;

    // --- Data
    const {
        streets,
        pointsOfInterest,
        poiTypeDomain,
        focusSelected,
        activePOI: active,
        focus,
        setPOIFocus,
        isFocusPOI,
        editPOI,
    } = getContext(dataKey);
    $: points = Object.keys($pointsOfInterest).map(
        (key) => $pointsOfInterest[key]
    );
    $: features = $streets.features || [];
    $: poiTypes = points.map((point) => {
        const type = point.type;
        return poiTypeDomain.includes(type) ? type : 'Other';
    });
    $: highlight = points.map((point) =>
        $focus.poi ? isFocusPOI($focus, point._key) : $active[point._key]
    );

    // --- Projection
    const projection = geoMercator()
        .scale(450000)
        .translate([0, 0])
        .rotate([-24.867, -36.069]);
    const path = geoPath(projection);
    $: coords = points.map((poi) => projection([poi.long, poi.lat]));

    // --- Scales
    const sizeScale = scaleLinear().range([3, 7]);
    $: sizeScale.domain(extent(points, (poi) => poi.num_visits));
    const colorScale = scaleOrdinal(schemeCategory10).domain(poiTypeDomain);

    // --- values Editing
    let svg;
    let tooltipPOI = null;
    let lastFocusPOI;
    let tooltipCoords = [0, 0];
    const type_ = writable('unknown');
    const location_ = writable('unknown');
    $: {
        if ($focus.poi) {
            $type_ = $focus.poi.type;
            $location_ = $focus.poi.locations.join('; ');
        } else {
            $type_ = 'unknown';
            $location_ = 'unknown';
        }
    }

    // --- Focus for editing
    function clickPOI(coords, poi) {
        tooltipCoords = computeTooltipPos(coords);
        setPOIFocus(poi);
        tooltipPOI = poi;
        lastFocusPOI = poi;
        $focusSelected = true;
    }

    function clickSVG(e) {
        if (!e.target.matches('circle')) {
            $focusSelected = false;
            setPOIFocus(null);
            tooltipPOI = null;
        }
    }

    function mouseEnterPOI(coords, poi) {
        if (!$focusSelected) {
            tooltipCoords = computeTooltipPos(coords);
            setPOIFocus(poi);
            tooltipPOI = poi;
            lastFocusPOI = poi;
        }
    }

    function mouseLeavePOI() {
        if (!$focusSelected) {
            setPOIFocus(null);
            tooltipPOI = null;
        }
    }

    function mouseEnterTooltip() {
        if (!$focusSelected) {
            setPOIFocus(lastFocusPOI);
            tooltipPOI = lastFocusPOI;
        }
    }

    function mouseLeaveTooltip() {
        if (!$focusSelected) {
            setPOIFocus(null);
            tooltipPOI = null;
        }
    }

    function clickTooltip() {
        $focusSelected = true;
    }

    function computeTooltipPos(coords) {
        const matrix = svg.getScreenCTM();
        const rect = svg.getBoundingClientRect();
        const p = svg.createSVGPoint();
        p.x = coords[0];
        p.y = coords[1];
        const screen = p.matrixTransform(matrix);
        return [screen.x - rect.x + 5, screen.y - rect.y - 88];
    }

    // --- Viewport / panning / zoom

    let el;
    let panning = false;
    let offset = [0, 0];
    let zoomScale = 1;
    $: viewbox = [
        zoomScale * (-width / 2 + offset[0]),
        zoomScale * (-height / 2 + offset[1]),
        zoomScale * width,
        zoomScale * height,
    ];

    function startPan(_) {
        panning = true;
    }

    function stopPan(_) {
        panning = false;
    }

    function pan(event) {
        if (panning) {
            offset = [offset[0] - event.movementX, offset[1] - event.movementY];
        }
    }

    function resetView(_) {
        zoomScale = 1;
        offset = [0, 0];
    }

    function zoom(event) {
        if (event.deltaY < 0) {
            zoomScale = Math.min(Math.max(zoomScale / zoomSpeed, 0.1), 2);
        } else {
            zoomScale = Math.min(Math.max(zoomScale * zoomSpeed, 0.1), 2);
        }
    }
</script>

<div bind:this={el} id="map" style="width:{width}px;height:{height}px;">
    <svg
        bind:this={svg}
        {width}
        {height}
        viewBox={`${viewbox[0]} ${viewbox[1]} ${viewbox[2]} ${viewbox[3]}`}
        on:mousedown={startPan}
        on:mouseup={stopPan}
        on:mousemove={pan}
        on:mousewheel|preventDefault={zoom}
        on:click={clickSVG}
        on:dblclick={resetView}
    >
        <g>
            {#each features as street, i}
                <path
                    fill="transparent"
                    stroke="currentColor"
                    stroke-width="0.5"
                    opacity="0.6"
                    d={path(street)}
                />
            {/each}
        </g>
        <g>
            {#each points as point, i}
                <circle
                    cx={coords[i][0]}
                    cy={coords[i][1]}
                    r={isFocusPOI($focus, point._key)
                        ? 7
                        : sizeScale(point.num_visits)}
                    opacity={highlight[i] ? 1 : 0.2}
                    fill={colorScale(poiTypes[i])}
                    on:mouseover={() => mouseEnterPOI(coords[i], point)}
                    on:mouseleave={mouseLeavePOI}
                    on:click={() => clickPOI(coords[i], point)}
                />
            {/each}
        </g>
    </svg>
    <svg id="legend" width={40} height={50}>
        <g font-size="10" font-family="sans-serif" text-anchor="begin">
            {#each poiTypeDomain as type, i}
                <rect
                    fill={colorScale(type)}
                    x="0"
                    y={i * 10 + 3}
                    width="5"
                    height="5"
                />
                <text x="7" y={i * 10 + 9} fill="currentColor">
                    {type}
                </text>
            {/each}
        </g>
    </svg>
    {#if tooltipPOI}
        <div
            id="tooltip"
            class="col"
            style="top:{tooltipCoords[1]}px;left:{tooltipCoords[0]}px;"
            on:mouseenter={mouseEnterTooltip}
            on:mouseleave={mouseLeaveTooltip}
            on:click={clickTooltip}
            transition:fade={{ duration: 50 }}
        >
            <span
                >Type: <input
                    type="text"
                    bind:value={$type_}
                    disabled={!$focus.poi}
                /></span
            >
            <span
                >Location: <input
                    type="text"
                    bind:value={$location_}
                    disabled={!$focus.poi}
                /></span
            >
            <button
                on:click={() => editPOI($type_, $location_)}
                disabled={!$focus.poi}>update</button
            >
        </div>
    {/if}
</div>

<style>
    #map {
        position: relative;
        margin-right: 5px;
    }

    #legend {
        position: absolute;
        top: 10px;
        right: 0;
    }
    #tooltip {
        padding: 5px;
        position: absolute;
        left: 0;
        top: 0;
        width: 150px;
        border: solid 1px #ddd;
        border-radius: 5px;
        background-color: #fff;
        margin-top: 5px;
    }

    span {
        font-size: 12px;
        font-family: sans-serif;
        width: 100%;
    }
    input {
        width: 55%;
        font-size: 12px;
        line-height: 12px;
        padding: 2px;
    }
    button {
        font-size: 12px;
        padding: 2px;
        margin-bottom: 0;
    }
</style>
